import PropTypes from 'prop-types';

import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {Batch, BatchSchema, StorableSummary} from 'constants/SampleConstants';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {
  BATCH_INFO_SHAPE,
  BatchInfo,
  BIOENTITY_INFO_SHAPE,
  TargetEntry,
  TargetFile,
  TargetStorable,
} from 'folder-items/FileConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LOOKUP_CONFIG_SHAPE} from 'lookup-config/LookupConfigModalConstants';
import {DataTableDataKey} from 'notebook/data-tables/DataTableConstants';
import {NOTE_PADDING} from 'notebook/notes/NoteConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  FilterInfo,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableMetadata,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  EvaluatedCell,
  EvaluatedCellError,
  EvaluatedTable,
  FullTableMap,
  OutputTableMap,
  Selector,
} from 'notebook/tables/excess/Evaluator';
import {SingleDocTableOpOutput} from 'notebook/tables/excess/ops/TableOps';
import {
  BASE_LOOKUP_DEFINITION_SHAPE,
  BaseLookupDefinition,
  LOOKUP_FIELD_RESULT_SHAPE,
  LookupFieldResult,
  SampleSpecifier,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {ResolveQueryFn, ResolveTypedValueToTypeFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {NonArrayTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {FulfillmentStatus} from 'requests/fulfillments/FulfillmentConstants';
import {REQUEST_TASK_GRAPHQL_SHAPE} from 'requests/tasks/RequestTaskConstants';
import {
  ParentStorableType,
  STORABLE_SCHEMA_SHAPE,
  PLATE_SCHEMA_SIZE_OPTIONS,
  PlateSize,
} from 'samples/storables/StorableSchemaConstants';
import {FIELD_TYPES, FieldType, SCHEMA_FIELD_SHAPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema, SchemaWithDescription} from 'schemas/SchemaConstants';
import {COMPUTED_STATE_SHAPE} from 'schemas/tags/computed/ComputedTagConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ArrayElement, Optional} from 'util/Types';
import {STAGE_RUN_FULL_SHAPE, STAGE_RUN_SAMPLE_FULL_SHAPE} from 'workflows/WorkflowConstants';

import {TokenUpdater} from './excess/ops/TokenUpdater';
import {QueryResolution} from './TableStoreTransforms';

// TODO(T11909): Copied from client/protocols/excess/Util.ts to avoid issues with snapshot build
export const VALUE_TYPES = {
  SCIENTIFIC: 'SCIENTIFIC',
  NUMBER: 'NUMBER',
  STRING: 'STRING',
  ARRAY: 'ARRAY',
  DATETIME: 'DATETIME',
};

export type TableIndex = [startIndex: number, endIndex: number];

export interface TableFunction {
  name: string;
  asyncOnClick?: (onCreateFormulaArgs: {
    rowData: RowData | null;
    evaluatedRow: Array<EvaluatedCell> | null;
    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
  }) => Promise<{formula: string; cursorPosition: number} | null>;
}

export interface TableSetting {
  name: string;
  applySettingToSelection: (
    args: MultiSelectionBox & {table: Table; onUpdateCells: (newCells: Array<Array<Cell>>) => void}
  ) => void;
}

// The range of cells that are selected
export interface MultiSelectionBox {
  minI: number;
  maxI: number;
  minJ: number;
  maxJ: number;
}

/**
 * The index ranges of the rows and columns that are fully selected.
 *
 * If one or more entire rows or columns are selected, the values are the [start, end] indices of the rows or
 * columns, inclusive. If only a subset of cells in any row or column are selected, the property will be
 * undefined.
 *
 * Both `row` and `column` are simultaneously defined if and only if all cells in the table are selected.
 * (Note that this is different from the table _itself_ being selected, since editor blocks -- paragraphs,
 * tables, etc. -- have an editor-level selection state independent of their contents.)
 *
 * Examples:
 *
 *     {column: [0, 0]}               // first column is fully selected
 *     {column: [0, 1]}               // first two columns are fully selected
 *     {row: [0, 2], column: [0, 1]}  // every cell is selected in a 2-column, 3-row table
 *     {}                             // some cells are selected, but not enough to make a full row or column
 *     {}                             // or, no cells are selected
 */
export interface MultiAxisDimSelection {
  row?: TableIndex;
  column?: TableIndex;
}

/**
 * Indexes of a cell in a table.
 *
 * Row: row index in table
 * Column: column index in table
 */
export interface CellIndex {
  row: number;
  column: number;
}

export type TableOpType =
  | 'shift-formulas'
  | 'update-cells'
  | 'upsert-cells'
  | 'insert-rows'
  | 'insert-cols'
  | 'delete-rows'
  | 'delete-cols'
  | 'insert-table'
  | 'delete-table'
  | 'rename-table'
  | 'replace-rows'
  | 'move-rows'
  | 'move-cols'
  | 'eval-table'
  | 'eval-columns'
  | 'toggle-single-input';

/** Base type for all table edit operations. */
interface BaseTableOpDiff {
  type: TableOpType;
}

/** Base type for table edit ops which may include formula shifted cells as a result of the core op. */
export interface BaseTableOpDiffWithOptionalFormulaShifts extends BaseTableOpDiff {
  shiftedCells?: Array<CellIndex>;
}

/** Op describing formula shifting in a table that was not directly modified. */
export interface ShiftFormulasTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'shift-formulas';
  shiftedCells: Array<CellIndex>;
}

/** An edit to one or more table cells. */
export interface UpdateCellsTableOp extends BaseTableOpDiff {
  type: 'update-cells';
  cells: Array<CellIndex>;
}

/** An edit to one or more table cells that may result in new rows or columns being appended. */
export interface UpsertCellsTableOp extends BaseTableOpDiff {
  type: 'upsert-cells';
  cells: Array<CellIndex>;
  newRowIndexes?: Array<number>;
  newColIndexes?: Array<number>;
}

/** Insert one or more table rows. */
export interface InsertRowsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'insert-rows';
  rowIndexes: Array<number>;
}

/** Insert one or more table columns. */
export interface InsertColumnsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'insert-cols';
  colIndexes: Array<number>;
}

/** Delete one or more table rows. */
export interface DeleteRowsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'delete-rows';
  rowIndexes: Array<number>; // Indexes reflect the table shape BEFORE the operation.
}

/** Delete one or more table columns. */
export interface DeleteColumnsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'delete-cols';
  colIndexes: Array<number>; // Indexes reflect the table shape BEFORE the operation.
}

/** Op to insert a single table. */
export interface InsertTableTableOp extends BaseTableOpDiff {
  type: 'insert-table';
}

/** Op to delete a single table. */
export interface DeleteTableTableOp extends BaseTableOpDiff {
  type: 'delete-table';
}

/** Op to rename a table. */
export interface RenameTableTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'rename-table';
  oldName: string;
}

/**
 * Delete one or more table rows and insert one or more table rows in a single operation.
 *
 * If only deletes or inserts are specified, this op is identical to delete-rows or
 * insert-rows, respectively. That means that deleted row indexes are relative to the
 * table before the mutation and inserted indexes are relative to the table after the
 * mutation.
 */
export interface ReplaceRowsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'replace-rows';
  deleteIndexes: Array<number>; // Indexes reflect the table shape BEFORE the operation.
  insertIndexes: Array<number>; // Indexes reflect the table shape AFTER the operation.
}

interface MoveRecord {
  from: number; // Index before all move operations.
  to: number; // Index after all move operations.
}

/** Move (AKA reorder) one or more table rows. */
export interface MoveRowsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'move-rows';
  rows: Array<MoveRecord>;
}

/** Move (AKA reorder) one or more table columns. */
export interface MoveColumnsTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'move-cols';
  cols: Array<MoveRecord>;
}

/** Resync table (results in all cells in table being evaluated). */
export interface EvalTableTableOp extends BaseTableOpDiff {
  type: 'eval-table';
}

/** Resync lookup columns (results in cells in specified columns being evaluated). */
export interface EvalColumnsTableOp extends BaseTableOpDiff {
  type: 'eval-columns';
  colIndexes: Array<number>;
}

/** Toggle one or more columns between single input and regular column state. */
export interface ToggleSingleInputTableOp extends BaseTableOpDiffWithOptionalFormulaShifts {
  type: 'toggle-single-input';
  /** Column move records describing how columns were reordered. */
  cols: Array<MoveRecord>;
  /** Cells that were modified by the merge/unmerge operation (in final column positions). */
  updatedCells: Array<CellIndex>;
}

/**
 * Object that captures an atomic change to a single table.
 */
export type TableOpDiff =
  | ShiftFormulasTableOp
  | UpdateCellsTableOp
  | UpsertCellsTableOp
  | InsertRowsTableOp
  | InsertColumnsTableOp
  | DeleteRowsTableOp
  | DeleteColumnsTableOp
  | InsertTableTableOp
  | DeleteTableTableOp
  | RenameTableTableOp
  | ReplaceRowsTableOp
  | MoveRowsTableOp
  | MoveColumnsTableOp
  | EvalTableTableOp
  | EvalColumnsTableOp
  | ToggleSingleInputTableOp;

/** Map of table edit operations for a single document. */
export interface TableOpDiffMap {
  [tableName: string]: TableOpDiff;
}
export type DocumentOp =
  | 'eval-document'
  | 'delete-document'
  | 'eval-document-missing-snapshot' // Indicates that a document was loaded with no eval snapshot.
  | 'eval-document-unsupported'; // Indicates evaluation of a document type that won't support op eval.

export type DocumentTableOp = TableOpDiffMap | DocumentOp;

export function isDocumentOp(op: DocumentTableOp): op is DocumentOp {
  return typeof op === 'string';
}

export function isTableOpDiffMap(op: DocumentTableOp): op is TableOpDiffMap {
  return op != null && typeof op !== 'string';
}

/** Map of document TableOps for a set of documents, such as a Worksheet. */
export interface MultidocTableOpMap {
  [docId: string]: DocumentTableOp;
}

/**
 * Map of formula shift operations for a single document.
 *
 * Used when executing table ops that mutate the shape of a table, resulting in formula shifts
 * being potentially applied. Creating a separate collection type to use during shifting is
 * ergonomic and helps us apply shifts on top of the primary table op.
 */
export interface ShiftFormulasTableOpDiffMap {
  [tableName: string]: ShiftFormulasTableOp;
}

/** Map of document ShiftFormulasTableOpDiffMap objects for a set of documents, such as a Worksheet. */
export interface MultidocShiftFormulasTableOpDiffMap {
  [docId: string]: ShiftFormulasTableOpDiffMap;
}

/**
 * Similar to FinalArrayTypedValue, but allows elements of the array to themselves be arrays. This is legal in
 * some intermediate data structures on the client, but the arrays are flattened before being sent to the
 * server.
 */
export interface ArrayTypedValue<SubTypedValue = TypedValue> {
  type: BaseTypedValueType.ARRAY;
  rawValue: Array<SubTypedValue>;
}

export type TypedValue = NonArrayTypedValue | ArrayTypedValue;

export interface BaseCellError {
  type: string;
  message: string;
}
export type BaseCellErrors = BaseCellError | Array<BaseCellError>;
export type PotentialCellErrors = BaseCellErrors | null | undefined;

// TODO(TypeScript): Merge ValueWithError and ValWithError
export type ValueWithError =
  | {
      value: TypedValue;
      error: Optional<BaseCellErrors>;
      locked: boolean;
      resolutions?: Array<QueryResolution>;
    }
  | {
      value: null;
      error: BaseCellErrors;
      locked: boolean;
      resolutions?: Array<QueryResolution>;
    };

export interface ValWithErrorObject {
  val: TypedValue;
  error: BaseCellErrors | null;
  locked: boolean;
}

export interface CustomEvalResult extends ValWithErrorObject {
  shouldSkipValidation?: boolean;
}

export type CustomEval<CustomEvalOptions> = (
  cell: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: CustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn,
  fileId?: string | null
) => CustomEvalResult;

export enum TableDimension {
  ROW = 'row',
  COLUMN = 'column',
}

export enum MaximumTableHeight {
  AUTO_FIXED = 'AUTO_FIXED',
  FULL = 'FULL',
}
export type MaximumTableHeightType = keyof typeof MaximumTableHeight | number;

export const TABLE_SELECTION_ROW = 'TABLE_SELECTION_ROW';
export const TABLE_SELECTION_COLUMN = 'TABLE_SELECTION_COLUMN';
export const TABLE_SELECTION_CELL = 'TABLE_SELECTION_CELL';
export const TABLE_MIN_COLUMNS = 1;
// arbitrary cutoff since we haven't verified performance above this size
export const TABLE_MAX_COLUMNS = 255;
export const TABLE_MIN_COLUMNS_WITH_GHOSTS = 15;
export const TABLE_EXTRA_GHOST_COLUMN_COUNT = 1;
export const TABLE_DEFAULT_COLUMN_WIDTH = 100;
export const TABLE_SMALL_COLUMN_WIDTH = Math.trunc((TABLE_DEFAULT_COLUMN_WIDTH * 3) / 4);
export const TABLE_MINI_COLUMN_WIDTH = Math.trunc(TABLE_DEFAULT_COLUMN_WIDTH / 2);
export const TABLE_WELL_DEFAULT_WIDTH = TABLE_MINI_COLUMN_WIDTH;
// Keep in sync with:
//  - table-cell.less =@table-row-height
//  - browser-tests/regions/notebook/entries/TableRegion.ts::addRowsByDragging::TABLE_ROW_HEIGHT
export const TABLE_ROW_HEIGHT = 24;
export const TABLE_PADDING = NOTE_PADDING; // keep in sync with variables.less =@mediocre-table-padding
export const TABLE_SELECTION_PADDING = 6;
export const TABLE_CELL_TOP_PADDING = 4; // Keep in sync with table-cell.less =@cell-top-padding
export const TABLE_FIRST_COL_WIDTH = 45;
export const TABLE_ROW = TableDimension.ROW;
export const TABLE_COLUMN = TableDimension.COLUMN;
export const TABLE_NAME_MAX_CHARS = 100;
export const TABLE_CELL_BG_IMPORTANT_COLOR = '#FEEFD1'; // yellow
export const TABLE_NAME_WELL_PREFIX = 'Well';
export const TABLE_AXIS_CELL_FONT_SIZE = 13;
export const TABLE_AXIS_CELL_HEIGHT = 31; // keep in sync with table-axis-cell.less =@table-axis-cell-height
export const TABLE_DOUBLE_SELECTED_BORDER_WIDTH = 2; // keep in sync with table-cell.less =@dbl-selected-border-width
export const TABLE_CELL_BORDER_WIDTH = 1; // keep in sync with table-cell.less =@table-cell-border-width
export const TABLE_TOOLTIP_DELAY = {show: 500, hide: 250};
export const TABLE_HEADER_HEIGHT = 37; // keep in sync with title-bar.less =@table-titlebar-height
export const FORMULA_BOX_HEIGHT_INLINE = 34; // keep in sync with formula-box.less =@table-formulabox-height-inline

export const TABLE_FOOTER_HEIGHT = 34; // keep in sync with table-footer.less =@table-footer-height
export const SCROLLBAR_WIDTH = 8; // keep in sync with variables.less =@scrollbar-width

export const DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT = 400;
// 25 default height rows is about the max visible rows in one screen height on an average screen
export const MINIMUM_ROWS_FOR_VIRTUALIZATION = 25;

// Sizes are of the form [# of rows, # of columns]
export const DEFAULT_TABLE_SIZE = [2, 2];
export const DEFAULT_PLATE_SIZE: PlateSize = [8, 12];
export const INSERTABLE_WELL_PLATE_SIZE_OPTIONS: Array<PlateSize> = [[32, 48], ...PLATE_SCHEMA_SIZE_OPTIONS];

/**
 * Properties of `Table` which are arrays whose length must match the number of rows.
 */
export type RowFields = Pick<Table, 'table' | 'rowData' | 'rowLabels'>;

/**
 * Properties of `Table` which are arrays whose length must match the number of columns.
 */
export const COLUMN_FIELD_DEFAULTS = {
  widths: TABLE_DEFAULT_COLUMN_WIDTH,
  columnConstraints: null,
  columnLabels: null,
} satisfies Partial<{
  [T in keyof TableData]: ArrayElement<TableData[T]>;
}>;

const LOOKUP_FIELD_RESULT_CACHED_SHAPE = PropTypes.shape({
  ...BASE_LOOKUP_DEFINITION_SHAPE,
  stageRunSampleId: PropTypes.string,
  stageRunId: PropTypes.string,
  fileId: PropTypes.string,
  batchId: PropTypes.string,
  results: PropTypes.arrayOf(LOOKUP_FIELD_RESULT_SHAPE.isRequired),
});
export type LookupFieldResultCached = BaseLookupDefinition &
  SampleSpecifier & {
    results: Array<LookupFieldResult>;
  };

export const externalCache = PropTypes.shape({
  LOOKUPFIELD: PropTypes.arrayOf(LOOKUP_FIELD_RESULT_CACHED_SHAPE.isRequired),
});

// These properties are not tied to the value of the cell and are preserved when backspacing on a cell.
export const CELL_FORMAT_PROPERTIES: Array<keyof Cell> = [
  'bold',
  'underline',
  'italic',
  'strikethrough',
  'selectorId',
  'precision',
  'align',
  'foregroundColor',
  'backgroundColor',
  'important',
  'scientificNotation',
  'mergeRowSpan',
  'mergeColSpan',
];

export const CELL_UI_PROPERTIES = [
  'formattedValue',
  'tdValue',
  'customEvalArgs',
  'customEval',
  'customEvalOptions',
  'locked',
  'error',
  'userAnnotationIndicator',
  'grayOutContents',
  'exportModifierText',
] as const;

export const ROW_UI_PROPERTIES = ['uiData'] as const;

export interface ExternalCache {
  LOOKUPFIELD: Array<LookupFieldResultCached | null>;
}

export enum BaseTypedValueType {
  // ERROR is a special type that is used to pass around errors in Tables, but should never actually be
  // persisted as a value.
  ERROR = 'ERROR',
  BATCH = 'BATCH',
  BIOENTITY = 'BIOENTITY',
  BOOLEAN = 'BOOLEAN',
  COMPUTING = 'COMPUTING',
  FULFILLABLE_STATUS = 'FULFILLABLE_STATUS',
  PARTIES = 'PARTIES',
  SAMPLE_RESTRICTION_STATUS = 'SAMPLE_RESTRICTION_STATUS',
  STAGE_RUN = 'STAGE_RUN',
  STORABLES = 'STORABLES',
  STORABLE_PLACEHOLDERS = 'STORABLE_PLACEHOLDERS',
  GRID_COORDINATES = 'GRID_COORDINATES',
  STAGE_RUN_SAMPLE = 'STAGE_RUN_SAMPLE',
  SCIENTIFIC = 'SCIENTIFIC',
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  ARRAY = 'ARRAY',
  DATETIME = 'DATETIME',
  DATE = 'DATE',
  JSON = 'JSON',
  CLIENT_IDENTIFIABLE = 'CLIENT_IDENTIFIABLE',
  LOOKUP_CONFIG_INFO = 'LOOKUP_CONFIG_INFO',
  PLATE_LAYOUT = 'PLATE_LAYOUT',
  USER = 'USER',
  TASK_STATUS = 'TASK_STATUS',
  PROCESS_TASK = 'WORKFLOW_TASK',
  PROCESS_OUTPUT = 'WORKFLOW_OUTPUT',
  ENTRY = 'ENTRY',
  STUDIES = 'STUDIES',
  PROCEDURE = 'PROCEDURE',
  PROCEDURE_SCHEMA_FIELD_TYPE = 'PROCEDURE_SCHEMA_FIELD_TYPE',
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',
  UNIT_REFERENCE = 'UNIT_REFERENCE',
  TEST_DEFINITION = 'TEST_DEFINITION',
}

export type TypedValueType = FieldType | BaseTypedValueType;

export function isTextEditableType(typedValue: TypedValue | null): boolean {
  if (typedValue == null) {
    return false;
  }
  const validTypes: Array<TypedValueType> = [
    BaseTypedValueType.STRING,
    BaseTypedValueType.NUMBER,
    BaseTypedValueType.JSON,
    FieldType.FLOAT,
    FieldType.INTEGER,
    FieldType.JSON,
    FieldType.LONG_TEXT,
    FieldType.TEXT,
  ];
  return validTypes.includes(typedValue.type as TypedValueType);
}

export const COMPUTING_TYPED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([BaseTypedValueType.COMPUTING]).isRequired,
  computedState: COMPUTED_STATE_SHAPE.isRequired,
});

export const PARTY_INFO_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
  partyType: PropTypes.oneOf(['USER', 'TEAM']).isRequired,
});

const STORABLE_INFO_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  barcode: PropTypes.string,
});

export const STORABLE_PLACEHOLDERS_TYPED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([BaseTypedValueType.STORABLE_PLACEHOLDERS]).isRequired,
  // A null value indicates a new storable.
  // The current use case is a container to be created in the bottom half of the transfer table.
  storablePlaceholders: PropTypes.arrayOf(STORABLE_INFO_SHAPE).isRequired,
});

export const STAGE_RUN_SAMPLE_TYPED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([BaseTypedValueType.STAGE_RUN_SAMPLE]).isRequired,
  stageRunSample: STAGE_RUN_SAMPLE_FULL_SHAPE,
});

export const USER_TYPED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([BaseTypedValueType.USER]).isRequired,
  id: PropTypes.string,
  avatarUrl: PropTypes.string,
  userHandle: PropTypes.string.isRequired,
  name: PropTypes.string,
});

// Depending on the type, this could have one of many other keys. For schema field types, the key matches
// that of the tag dict. See DataTypes.convertTagToTypedValue, e.g. {type: 'ft_integer', integerValue: 123}
export const TYPED_VALUE_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.BIOENTITY]).isRequired,
    bioentitySummary: BIOENTITY_INFO_SHAPE.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.BATCH]).isRequired,
    batchInfo: BATCH_INFO_SHAPE.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.BOOLEAN]).isRequired,
    booleanValue: PropTypes.bool.isRequired,
  }),
  COMPUTING_TYPED_VALUE_SHAPE,
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.FULFILLABLE_STATUS]).isRequired,
    status: PropTypes.oneOf<FulfillmentStatus>(Object.values(FulfillmentStatus)),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PARTIES]).isRequired,
    partySummaries: PropTypes.arrayOf(PARTY_INFO_SHAPE.isRequired).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.SAMPLE_RESTRICTION_STATUS]).isRequired,
    status: PropTypes.oneOf(Object.values(SampleRestrictionStatus)).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.STAGE_RUN]).isRequired,
    stageRunId: PropTypes.string.isRequired,
    stageRunName: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.STORABLES]).isRequired,
    storableSummaries: PropTypes.arrayOf(STORABLE_INFO_SHAPE.isRequired).isRequired,
  }),
  STORABLE_PLACEHOLDERS_TYPED_VALUE_SHAPE,
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.GRID_COORDINATES]).isRequired,
    gridCoordinates: PropTypes.shape<PropTypes.ValidationMap<{columnIndex: number; rowIndex: number}>>({
      columnIndex: PropTypes.number.isRequired,
      rowIndex: PropTypes.number.isRequired,
    }),
  }),
  STAGE_RUN_SAMPLE_TYPED_VALUE_SHAPE,
  PropTypes.shape({
    lookupConfig: LOOKUP_CONFIG_SHAPE,
    source: LOOKUP_CONFIG_SHAPE.isRequired,
    destination: LOOKUP_CONFIG_SHAPE,
  }),
  USER_TYPED_VALUE_SHAPE,
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.TASK_STATUS]).isRequired,
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    statusType: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PROCESS_TASK]).isRequired,
    id: PropTypes.string.isRequired,
    humanId: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PROCESS_OUTPUT]).isRequired,
    id: PropTypes.string.isRequired,
    humanId: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.ENTRY]).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
    humanId: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.DATE]).isRequired,
    value: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.JSON]).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    jsonValue: PropTypes.any,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.CLIENT_IDENTIFIABLE]).isRequired,
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.STUDIES]).isRequired,
    studies: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        humanId: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PROCEDURE]).isRequired,
    procedures: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE]).isRequired,
    displayName: PropTypes.string.isRequired,
    fieldDefinition: PropTypes.shape({
      fieldType: PropTypes.oneOf([FieldType.TEXT, FieldType.BIOENTITY_LINK, FieldType.FLOAT]).isRequired,
      linkedFolderItemType: PropTypes.string,
      linkedTagSchemaId: PropTypes.string,
    }).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES]).isRequired,
    conditionReplicates: PropTypes.arrayOf(
      PropTypes.shape({
        procedureMethodExecutionInstanceId: PropTypes.string.isRequired,
        conditionName: PropTypes.string.isRequired,
        replicateNumber: PropTypes.number.isRequired,
      })
    ).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.UNIT_REFERENCE]).isRequired,
    unitSummary: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      symbol: PropTypes.string.isRequired,
    }).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([BaseTypedValueType.TEST_DEFINITION]).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  // All other types have unspecified keys for now, but these should be filled in.
  PropTypes.shape({
    type: PropTypes.oneOf([
      ...Object.values(VALUE_TYPES),
      ...Object.values(FIELD_TYPES).map(({type}) => type),
    ]).isRequired,
  }),
]);

export type PastedCell = Cell & {
  selector?: Selector;
  parentStorableSummary?: StorableSummary;
  batch?: Batch;
  formula?: string;
};

export type FormulaAlignment = 'left' | 'right' | 'center';

export interface Cell<CustomEvalOptions = unknown> {
  // If a cell has a typedValue, it should have an empty text string. (There may well
  // be old code that does not honor this. New code certainly should.)
  text: string;
  typedValue?: TypedValue | null;
  link?: Optional<string>;
  // this is set, on save, by client/notes/augmentNotePartTablesWithValues.js
  cachedTypedValue?: Optional<TypedValue>;
  cachedValue?: Optional<string>;
  bold?: Optional<boolean>;
  underline?: Optional<boolean>;
  italic?: Optional<boolean>;
  strikethrough?: Optional<boolean>;
  selectorId?: Optional<string>;
  precision?: Optional<number>;
  align?: Optional<FormulaAlignment>;
  foregroundColor?: Optional<string>;
  backgroundColor?: Optional<string>;
  grayOutContents?: Optional<boolean>;
  important?: Optional<boolean>;
  scientificNotation?: Optional<boolean>;
  // `mergeRowSpan` and `mergeColSpan` are the number of merged rows
  // and columns respectively.
  // Only the top left cell in a merged region should have these
  // properties set.
  mergeRowSpan?: number;
  mergeColSpan?: number;
  // Maps <schemaName> + <fieldName> -> cached results
  externalCache?: ExternalCache;
  itemLinks?: Array<BatchInfo> | Array<TargetFile> | Array<TargetEntry> | Array<TargetStorable> | null;
  schemaFieldSelectorOptionIds?: Array<string>;

  // Overrides for using a <TableEditable> for presentational purposes (should not be persisted):
  formattedValue?: Optional<JSX.Element>;
  tdValue?: Optional<JSX.Element>;
  // customEvalArgs is an array of formulas which will be passed into customEval, which is called in place
  // of normal ASTNode.eval via SpreadsheetEvaluator. This is a powerful feature for custom tables that
  // would like to construct a cell that is evaluated like an excess function would (has parameters that
  // can reference other cells), but does /not/ require there to be a user-facing excess function, as we
  // can use a custom JS function via customEval, e.g. RegistrationTableUtil.setFieldsForExistingBioentity
  customEvalArgs?: Optional<Array<string | null>>;
  customEval?: Optional<CustomEval<CustomEvalOptions>>;
  customEvalOptions?: CustomEvalOptions;
  // Declare that this individual cell cannot be edited, and should have a gray background.
  // Currently only used on the frontend and will error if persisted to the server.
  locked?: Optional<boolean>;
  // Currently only used on the frontend
  error?: Optional<EvaluatedCellError>;
  userAnnotationIndicator?: Optional<JSX.Element>;
  exportModifierText?: string;
}

export const cell = PropTypes.shape({
  text: PropTypes.string.isRequired,
  typedValue: TYPED_VALUE_SHAPE,
  link: PropTypes.string,
  // this is set, on save, by client/notes/augmentNotePartTablesWithValues.js
  typedCachedValue: TYPED_VALUE_SHAPE,
  cachedValue: PropTypes.string,
  bold: PropTypes.bool,
  underline: PropTypes.bool,
  italic: PropTypes.bool,
  strikethrough: PropTypes.bool,
  selectorId: PropTypes.string,
  precision: PropTypes.number,
  align: PropTypes.string,
  foregroundColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  grayOutContents: PropTypes.bool,
  important: PropTypes.bool,
  // `mergeRowSpan` and `mergeColSpan` are the number of merged rows
  // and columns respectively.
  // Only the top left cell in a merged region should have these
  // properties set.
  mergeRowSpan: PropTypes.number,
  mergeColSpan: PropTypes.number,
  // Maps <schemaName> + <fieldName> -> cached results
  externalCache,
  itemLinks: PropTypes.array,

  // Overrides for using a <TableEditable> for presentational purposes (should not be persisted):
  formattedValue: PropTypes.node,
  tdValue: PropTypes.node,
  // customEvalArgs is an array of formulas which will be passed into customEval, which is called in place
  // of normal ASTNode.eval via SpreadsheetEvaluator. This is a powerful feature for custom tables that
  // would like to construct a cell that is evaluated like an excess function would (has parameters that
  // can reference other cells), but does /not/ require there to be a user-facing excess function, as we
  // can use a custom JS function via customEval, e.g. RegistrationTableUtil.setFieldsForExistingBioentity
  customEvalArgs: PropTypes.arrayOf(PropTypes.string),
  customEval: PropTypes.func,
  // Declare that this individual cell cannot be edited, and should have a gray background.
  // Currently only used on the frontend and will error if persisted to the server.
  locked: PropTypes.bool,
  userAnnotationIndicator: PropTypes.node,
});

/**
 * Properties of Cell objects that should trigger evaluation if changed.
 *
 * Only includes properties that may be changed directly as a single operation.
 * (i.e. excludes merged range dims, custom eval fields, etc.)
 */
export const EVALUABLE_CELL_PROPERTIES: Array<string> = ['text', 'typedValue', 'selectorId'];

export const PLACEHOLDER_CELL: Cell = {text: '--', align: 'center'};

export interface MultiDocumentCellColors {
  [documentName: string]: CrossTableCellColors;
}
export interface CrossTableCellColors {
  [tableName: string]: CellColors;
}
export interface CellColors {
  [rowIndex: number]: {[columnIndex: number]: string};
}

export type RawValue = string | number | boolean | TypedValue | null;

export type Row = Array<Cell>;
export const row = PropTypes.arrayOf(cell.isRequired);

export type Cells = Array<Row>;
export const cells = PropTypes.arrayOf(row.isRequired);

/**
 * A 1-dimensional selection range on either the row or column axis.
 *
 * @see MultiAxisDimSelection for an explanation of the indexing.
 */
export interface SelectionRange {
  minIndex: number;
  maxIndex: number;
}

const widths = PropTypes.arrayOf(PropTypes.number.isRequired);

// Keep in sync with TableColumnType in structured_table_column.py
export enum TableColumnType {
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
  TAG_VALUE = 'TAG_VALUE',

  // Registration table columns
  BATCH = 'BATCH',
  // WARNING: The following 6 types are no longer in use for new tables but should be retained until existing
  // tables have been migrated to the new unified BASES type.
  DNA_BASES = 'DNA_BASES',
  RNA_BASES = 'RNA_BASES',
  DNA_HELM = 'DNA_HELM',
  RNA_HELM = 'RNA_HELM',
  DNA_IDT = 'DNA_IDT',
  RNA_IDT = 'RNA_IDT',
  BASES = 'BASES',
  // Whether a DNA sequence is linear or circular.
  SEQUENCE_TOPOLOGY = 'SEQUENCE_TOPOLOGY',
  SMILES = 'SMILES',
  STAGE_RUN = 'STAGE_RUN',
  EXISTING_BIOENTITY = 'EXISTING_BIOENTITY',
  // The BioentitySelector column mimics a TagValue/Bioentity field, but doesn't require the cell to be a schema field
  BIOENTITY_SELECTOR = 'BIOENTITY_SELECTOR',
  BIOENTITY_NAME = 'BIOENTITY_NAME',
  ALIAS = 'ALIAS',

  // Inventory table columns
  NUMBER_OF_CONTAINERS = 'NUMBER_OF_CONTAINERS',
  CONTAINERS = 'CONTAINERS',
  PARENT_STORABLE = 'PARENT_STORABLE',
  POSITION = 'POSITION',
  QUANTITY = 'VOLUME',
  CONCENTRATION = 'CONCENTRATION',
  SAMPLE = 'SAMPLE',
  PLATE = 'PLATE',
  BOX = 'BOX',
  PARTIES = 'PARTIES',
  SAMPLE_RESTRICTION_STATUS = 'SAMPLE_RESTRICTION_STATUS',

  // Process columns
  PROCESS_TASK = 'PROCESS_TASK',
  PROCESS_TASK_ASSIGNEE = 'PROCESS_TASK_ASSIGNEE',
  PROCESS_TASK_SCHEDULED_ON_DATE = 'PROCESS_TASK_SCHEDULED_ON_DATE',
  PROCESS_TASK_STATUS = 'PROCESS_TASK_STATUS',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',

  // Mixture table columns
  MEASUREMENT_AMOUNT = 'MEASUREMENT_AMOUNT',
  INGREDIENT_CATALOG_ID = 'INGREDIENT_CATALOG_ID',
  // Complex polymer columns
  COMPLEX_POLYMER_COMPONENT = 'COMPLEX_POLYMER_COMPONENT',
  COMPLEX_POLYMER_CONNECTIONS = 'COMPLEX_POLYMER_CONNECTIONS',
  OLIGO_DUPLEX_OFFSET = 'OLIGO_DUPLEX_OFFSET',
  // TODO(BNCH-17431): Update string value to match variable name after migration on structured_table_column
  INGREDIENT_COMPONENT_LOT_TEXT = 'INGREDIENT_SOURCE',
  INGREDIENT_NOTES = 'INGREDIENT_NOTES',

  // Bulk assembly columns
  BULK_ASSEMBLY_FRAGMENT_POLYMER = 'BULK_ASSEMBLY_FRAGMENT_POLYMER',
  BULK_ASSEMBLY_BIN_SELECTOR = 'BULK_ASSEMBLY_BIN_SELECTOR',
  BULK_ASSEMBLY_FRAGMENT_ENDPOINT = 'BULK_ASSEMBLY_FRAGMENT_ENDPOINT',
  BULK_ASSEMBLY_LENGTH = 'BULK_ASSEMBLY_LENGTH',
  BULK_ASSEMBLY_CONSTRUCT_NAME = 'BULK_ASSEMBLY_CONSTRUCT_NAME',
  BULK_ASSEMBLY_VALIDATION_STATUS = 'BULK_ASSEMBLY_VALIDATION_STATUS',
  BULK_ASSEMBLY_PRIMER = 'BULK_ASSEMBLY_PRIMER',
  BULK_ASSEMBLY_PRIMER_BASES = 'BULK_ASSEMBLY_PRIMER_BASES',
  BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR = 'BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR',
  BULK_ASSEMBLY_PREFERRED_PRIMER = 'BULK_ASSEMBLY_PREFERRED_PRIMER',
  BULK_ASSEMBLY_RESTRICTION_ENZYME = 'BULK_ASSEMBLY_RESTRICTION_ENZYME',

  // Plate map columns
  ANONYMOUS_TAG_VALUE = 'ANONYMOUS_TAG_VALUE',
  EXPERIMENTAL_ROLE = 'EXPERIMENTAL_ROLE',
  FILL_PLATE_MEASUREMENT_AMOUNT = 'FILL_PLATE_MEASUREMENT_AMOUNT',
  FILL_PLATE_TRANSFER_SOURCE_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_CHIP',
  FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP',
  FILL_PLATE_TRANSFER_SOURCE_ICON = 'FILL_PLATE_TRANSFER_SOURCE_ICON',
  FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP = 'FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP',
  PLATE_MAP_MATERIAL_INPUT = 'PLATE_MAP_MATERIAL_INPUT',

  // Procedure columns
  PROCEDURE_PARAMETER_CONFIRMED_IN = 'PROCEDURE_PARAMETER_CONFIRMED_IN',
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',

  // Other columns
  // [Deprecated] Contains legacy hard-coded unit strings
  UNIT_SELECTOR = 'UNIT_SELECTOR',
  // [Preferred] Contains references to Unit Dictionary models
  UNIT_REFERENCE = 'UNIT_REFERENCE',
  DEDICATED_LOOKUP = 'DEDICATED_LOOKUP',
  CUSTOM = 'CUSTOM',
  STUDIES = 'STUDIES',
  ROW_DELEGATE = 'ROW_DELEGATE',

  // Additional columns that aren't persisted yet.  If these are ever
  // to be persisted, we should add them to the TableColumnType enum
  // on the backend.
  FULFILLABLE_STATUS = 'FULFILLABLE_STATUS',
  REQUEST_TASK_SELECTION = 'REQUEST_TASK_SELECTION',
  SAMPLE_GROUP_SAMPLE = 'SAMPLE_GROUP_SAMPLE',
  STAGE_RUN_SAMPLE = 'STAGE_RUN_SAMPLE',
  OPTION_SELECTOR = 'OPTION_SELECTOR',
  OPTION_SEARCH_SELECTOR = 'OPTION_SEARCH_SELECTOR',
  LOOKUP_CONFIG = 'LOOKUP_CONFIG',
  PLATE_LAYOUT_SELECTOR = 'PLATE_LAYOUT_SELECTOR',
  PROCESS_TASK_EXECUTION_ORIGIN = 'PROCESS_TASK_EXECUTION_ORIGIN',
  PROCEDURE = 'PROCEDURE',
  PROCEDURE_SCHEMA_FIELD_TYPE = 'PROCEDURE_SCHEMA_FIELD_TYPE',
  // If the tag schema field has been removed, then this column type will be used
  MISSING_FIELD = 'MISSING_FIELD',
  TEST_DEFINITION = 'TEST_DEFINITION',

  // Antibody columns
  ANTIBODY_DOMAIN = 'ANTIBODY_DOMAIN',
}

// Table search result path constants
export const TABLE_SEARCH_PATHS = {
  NAME: 'name',
  TABLE_HEADER_ROW: 'tableHeaderRow',
} as const;

export const COLUMN_DEFINITION_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ALIAS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BATCH]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BIOENTITY_NAME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BOOLEAN]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BOX]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CONCENTRATION]),
    units: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CONTAINERS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DEDICATED_LOOKUP]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.CUSTOM]),
    // There are other properties not declared here, but they are only accessed in TypeScript code,
    // so they don't need prop types
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.EXISTING_BIOENTITY]),
    tagSchemaOrInterface: PropTypes.oneOfType([
      RegistryConstants.SCHEMA_SUMMARY_SHAPE,
      PropTypes.oneOf(['ALL']),
    ]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BIOENTITY_SELECTOR]),
    schemaFieldForFiltering: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FULFILLABLE_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.MISSING_FIELD]),
    structuredTableColumnName: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.NUMBER_OF_CONTAINERS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PARENT_STORABLE]),
    isForTransfer: PropTypes.bool.isRequired,
    // Schema of storables that will go in the parent storable
    storableSchema: STORABLE_SCHEMA_SHAPE,
    // Whether the parent storable is a Grid or a Location.
    // If null, both are allowed when not using inline location selector.
    // DEPRECATED - prefer parentStorableType for new structured tables.
    parentStorableIsGrid: PropTypes.bool,
    // Allow Grid, Location or both for parent storable in inline location selector
    parentStorableType: PropTypes.oneOf(Object.values(ParentStorableType)),
    fallbackRegistryId: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PARTIES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.POSITION]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.REQUEST_TASK_SELECTION]),
    requestTask: REQUEST_TASK_GRAPHQL_SHAPE.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE_GROUP_SAMPLE]),
    schemaField: SCHEMA_FIELD_SHAPE,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SAMPLE_RESTRICTION_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STRING]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.TAG_VALUE]),
    schemaField: SCHEMA_FIELD_SHAPE,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STAGE_RUN]),
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.UNIT_SELECTOR]),
    acceptedUnits: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.OPTION_SELECTOR]),
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          node: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
          onUpdateKey: PropTypes.string,
        }),
      ])
    ),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.OPTION_SEARCH_SELECTOR]),
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        }),
      ])
    ),
    isMulti: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.MEASUREMENT_AMOUNT]),
    name: PropTypes.string,
    isComputed: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_CATALOG_ID]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.INGREDIENT_NOTES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.QUANTITY]),
    units: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ANONYMOUS_TAG_VALUE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.EXPERIMENTAL_ROLE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE_MAP_MATERIAL_INPUT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_LENGTH]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PRIMER]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_PRIMER_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.LOOKUP_CONFIG]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE]),
    procedureTypes: PropTypes.arrayOf(PropTypes.string.isRequired),
    placeholder: PropTypes.string,
    shouldAllowMultiple: PropTypes.bool,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_CONDITION_REPLICATES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PLATE_LAYOUT_SELECTOR]),
    runSchema: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_ASSIGNEE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_STATUS]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE]),
    context: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.TEST_DEFINITION]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.PROCESS_OUTPUT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_HELM]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_HELM]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.DNA_IDT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.RNA_IDT]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.BASES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SMILES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.STUDIES]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ROW_DELEGATE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.UNIT_REFERENCE]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.SEQUENCE_TOPOLOGY]),
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([TableColumnType.ANTIBODY_DOMAIN]),
    domainType: PropTypes.oneOf(Object.values(AntibodyDomainType)),
    specificity: PropTypes.string,
  }),
]);

export interface TooltipIcon {
  icon: BenchlingIconName;
  tooltip: Optional<string>;
}

export interface ColumnConstraint<TColumnType = StructuredTableColumn> {
  type?: typeof SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD;
  columnDefinition?: ColumnDefinition | null;
  structuredTableColumn?: TColumnType;
  // Tag schema field used for some specialized cases when a render strategy is unspecified.
  tagSchemaFieldId?: Optional<number>;
  tagSchemaField?: SchemaField;

  // Used by plain tables to lock columns
  isProtected?: boolean;

  // Controls whether the column may be deleted/reinserted by the user.
  // Supercedes the legacy behavior for column insertion/deletion controled by the deprecated field
  // TableViewProps.legacyFirstEditableColumnIndex. When this field is set, that field will be ignored.
  isColumnUserConfigurable?: boolean;

  // Currently not persisted and used by DataTables for threading render information for columns
  tooltipIcons?: Array<TooltipIcon>;
  renderedLabel?: React.ReactNode;
  userAnnotationIndicator?: Optional<JSX.Element>;
  tooltip?: Optional<string>;
  canResetLabel?: Optional<boolean>;
  editLabelDisabled?: boolean;
  viewer?: boolean;
}

export const columnConstraint = PropTypes.shape({
  type: PropTypes.oneOf([SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD]),
  columnDefinition: COLUMN_DEFINITION_SHAPE,
  // Tag schema field used for some specialized cases when a render strategy is unspecified.
  tagSchemaFieldId: PropTypes.number,
  tagSchemaField: SCHEMA_FIELD_SHAPE,

  // Currently not persisted and used by DataTables for threading render information for columns
  tooltipIcons: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
    }).isRequired
  ),
  renderedLabel: PropTypes.node,
  userAnnotationIndicator: PropTypes.node,
  tooltip: PropTypes.string,
  canResetLabel: PropTypes.bool,
  viewer: PropTypes.bool,
  editLabelDisabled: PropTypes.bool,
});

export interface RowDataUiData {
  userAnnotationIndicator?: Optional<JSX.Element>;

  // Whether the row includes changes that have not yet been submitted, such as in a structured table row that
  // has been edited since the last submission. For explicitness, undefined may be used to indicate that the
  // unsubmittedness of the row is unknown and the UI may choose to render or not render indicators.
  hasUnsubmittedChanges?: boolean;

  /**
   * Custom text to display in the row axis tooltip, if the row has unsubmitted changes.
   * If not provided the default text will be used.
   */
  unsubmittedChangesTooltip?: string | null;

  // The archive purpose of the row, if archived, otherwise null.
  // If undefined, this means the archived state is unknown, and we should render using the same logic as
  // if it were null.
  //
  // Note that this represents the upstream (live) state of the row's archivePurpose, which is what we choose
  // to render in the UI.
  archivePurpose?: string | null;
}

export interface RowMetadata {
  rowId?: string;
  isFinalized?: boolean;
  // map of visible column provenance uuid to delegated column provenance uuid
  delegatedColumns?: {[k: string]: string};
}

export type RowData = DataTableDataKey & {
  // The euid is used for DataTables and ResultsTables
  euid?: Optional<string>;
  // The rowId is used for StructuredTables
  rowId?: Optional<string>;
  fulfillmentId?: Optional<string>;
  isFinalized?: Optional<boolean>;
  sampleGroupId?: Optional<string>;

  // Set to preserve the StructuredTable <-> Table bijection, but should not be used by the UI directly.
  readonly lastSyncedUpstreamValues?: StructuredTableRow<unknown>['lastSyncedUpstreamValues'];

  // These properties are added to row data objects on the client side and should be stripped away
  // before saving to the store / persisting to the server.
  uiData?: RowDataUiData;

  // RowMetadata from the StructuredTable which can be round-tripped back to the StructuredTableRow when
  // converting back from Table -> StructuredTable.
  rowMetadata?: RowMetadata;

  // Archive purpose from the StructuredTable row. This is only to allow the archival state to be immediately
  // loaded on table render. The live value should be used whenever possible as fetched from the row's primary sample's
  // archive record
  lastSyncedArchivePurpose?: Optional<string>;

  // If the table has any row delegate columns, the delegated columns will be stored on the table, and a map
  // of actual column to delegated column (using provenance_uuids) will exist on the row metadata. These
  // columns should be turned into ColumnConstraints when converting to the TableData interface. The TableRow
  // component will look here for the column constraint to pass to the TableCell component for any cell in
  // a row delegate column.
  delegatedColumnConstraints?: Optional<Map<string, ColumnConstraint>>;
};

export const ROW_DATA_SHAPE = PropTypes.shape({
  euid: PropTypes.string,
  stageRunSampleId: PropTypes.string,
  batchId: PropTypes.string,
  stageRunId: PropTypes.string,
  isFinalized: PropTypes.bool,
  userAnnotationIndicator: PropTypes.node,
});

export interface RowProperties {
  hasErrors?: boolean;
  hasUnsubmittedChanges?: boolean;
  hasWarnings?: boolean;
  unsubmittedChangesTooltip?: string | null;
  isOutdated?: boolean;
  archivePurpose?: string | null;
  isTombstoned?: boolean;
  tombstoneReasonTooltip?: string | null;
}

export const ROW_PROPERTIES_SHAPE = PropTypes.shape({
  hasErrors: PropTypes.bool,
  hasUnsubmittedChanges: PropTypes.bool,
  isOutdated: PropTypes.bool,
  archivePurpose: PropTypes.string,
  isTombstoned: PropTypes.bool,
  tombstoneReasonTooltip: PropTypes.string,
});

// These are attributes that we need to convert a TableData into a RegistrationTable
export interface RegistrationTableMetadata {
  labelingStrategy: RegistryLabelingStrategy | null;
}

export interface TransferTableMetadata {
  isPlaceholderTable: boolean;
}

// This interface contains everything that's needed to complete the
// inversion of the bijection between StructuredTable and TableData.
export interface StructuredTableToTableMetadata<TTableMetadata> {
  cid: string;
  kind: StructuredTableKind;
  provenanceUuid: string;
  fileId?: string;
  apiId?: string;
  numApiRowsToAdd?: number;
  schemas: Array<Schema | BatchSchema | SchemaWithDescription>;
  metadata: StructuredTableMetadata & TTableMetadata;
  // Is the StructuredTable in a template or not
  isConfig: boolean;
}

// TableMetadata contains the attributes that we need to be able to round-trip from
// a particular structured table into a TableData and then come back without losing
// any information.  This is important so we keep atomic updates and it reduces
// complexity for having to merge together the old version of the structured table
// and the updated table.
export type TableMetadata =
  | RegistrationTableMetadata
  | TransferTableMetadata
  | StructuredTableToTableMetadata<unknown>;

// The model of the table that may be transformed into the format to be persisted
export interface TableData {
  name: string;
  provenanceUuid?: string | null;
  apiId?: string;
  fileId?: string;
  numApiRowsToAdd?: number;
  widths: Array<number>;
  table: Cells;
  // rowData will always be present in structured tables
  rowData?: Optional<Array<RowData>>;
  columnConstraints: Array<ColumnConstraint | null>;
  columnLabels: Array<string | null>;
  metadata?: TableMetadata;
  filterInfo?: FilterInfo; // TODO: make this required when column added to database and integrated in adapter BNCH-99203
}

export interface TableHeaderControlsArgs {
  multiSelectionBox: MultiSelectionBox | null;
  computedTable: EvaluatedTable;
  isCollapsed: boolean;
}
export type TableHeaderControlsCreator = (args: TableHeaderControlsArgs) => JSX.Element | null;
export type TableHeaderControls = JSX.Element | TableHeaderControlsCreator;
export type TableFooterControls = JSX.Element;

export interface TableDimensionLocks {
  allowAddRows: boolean;
  allowRemoveRows: boolean;
  allowEditCols: boolean;
}

export interface TableReorderLocks {
  [TableDimension.ROW]: boolean;
  [TableDimension.COLUMN]: boolean;
}

export const TABLE_REORDER_LOCKS_SHAPE = PropTypes.shape({
  [TableDimension.ROW]: PropTypes.bool,
  [TableDimension.COLUMN]: PropTypes.bool,
});

// Properties required by the TableEditable (in addition to TableData) for rendering
export interface TableViewProps {
  renderedName?: JSX.Element | string;
  nameTooltip?: string | null;
  headerRow?: Optional<boolean>;
  headerCol?: Optional<boolean>;
  rowLabels?: Optional<Array<JSX.Element | string>>;

  // Locked dims is in an in-between state as we work to refactor it, and recover from INCDT-518.
  // If you need to set it, use a boolean; if you need to read it, use TableUtil.crossCompatibleLockedDimsCheck
  lockedDims?: Optional<boolean | TableDimensionLocks>; // prevent edits to dimensions of table
  allowColumnEdits?: Optional<boolean>; // despite lockedDims, allow insert / deletion of columns
  maxRows?: number; // Maximum number of rows to allow regardless of collapsed or expanded state.

  allowAddRows?: Optional<boolean>;
  allowNoRows?: Optional<boolean>;
  allowRowRemoval?: Optional<boolean>;
  // Number of columns to render affixed when scrolling. This is currently only be used on the front-end,
  // and persisting it is not yet persisted. Note that this should not include columns that are editable.
  // See T12483.
  affixedColumnCount?: Optional<number>;
  disallowCustomColumns?: Optional<boolean>;

  /**
   * @deprecated Hack for inserting and deleting columns. Used ONLY for legacy tables.
   * Replaced by the isColumnUserConfigurable field on ColumnConstraint.
   */
  legacyFirstEditableColumnIndex?: Optional<number>;

  // Hack to hide the "Rename Table" option on the header context menu.
  // In the future, all tables should support rename.
  _hideRename?: Optional<boolean>;
  // Hack to let the spreadsheet evaluator know what type of table it's in
  _tableType?: Optional<string>;
  _rowHeader?: Optional<JSX.Element | string>;
  _firstColumnWidth?: number | null;
  _disableInsertColsTooltip?: string;
}

/**
 * Primary data object for TableEditable components, as well as the spreadsheet evaluation system.
 *
 * TODO(BNCH-35010): Deep equality must be preserved for this object and its sub-objects or you will
 * break spreadsheet evaluation!
 */
export type Table = TableData & TableViewProps;

export const table = PropTypes.shape({
  widths: widths.isRequired,
  columnConstraints: PropTypes.arrayOf(columnConstraint).isRequired,
  table: cells.isRequired,
  headerRow: PropTypes.bool,
  headerCol: PropTypes.bool,
  rowData: PropTypes.arrayOf(ROW_DATA_SHAPE.isRequired),

  lockedDims: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      allowAddRows: PropTypes.bool,
      allowRemoveRows: PropTypes.bool,
      allowEditCols: PropTypes.bool,
    }),
  ]), // prevent edits to dimensinons of table
  allowColumnEdits: PropTypes.bool, // despite lockedDims, allow insert / deletion of columns

  // Number of columns to render affixed when scrolling. This is currently only be used on the front-end,
  // and persisting it is not yet persisted. Note that this should not include columns that are editable.
  // See T12483.
  affixedColumnCount: PropTypes.number,
  // Hack to hide the "Rename Table" option on the header context menu.
  // In the future, all tables should support rename.
  _hideRename: PropTypes.bool,
  // Hack to let the spreadsheet evaluator know what type of table it's in
  _tableType: PropTypes.string,
});

// Can be threaded into TableOps.insertColumns(Left|Right) to insert columns with column fields set.
// Note that the key names match the exact keys in TableConstants.table, despite being
// only mapped to a single value.
export interface InsertableColumnInfo<TColumnType = StructuredTableColumn> {
  columnLabels: string;
  columnConstraints?: ColumnConstraint<TColumnType>;
  widths?: number;
  dataTestAttrs?: DataTestAttrs;
}

// Can be threaded into TableOps.insertColumns(Left|Right) to insert columns with column fields set.
// Note that the key names match the exact keys in export const PropTypes.table, despite being
// only mapped to a single value.
export const insertableColumnInfo = PropTypes.shape({
  widths: PropTypes.number,
  columnConstraints: columnConstraint,
  columnLabels: PropTypes.string,
});

export const UPDATE_LOOKUP_FIELD_MESSAGE = 'Update lookup field values';
export const RESYNC_TABLE_LABEL = 'Re-sync table';
export const REFRESH_ALL_OPTION = 'Refresh all';
export const REFRESH_LOOKUP_OPTION = 'Refresh lookup values';
export const REFRESH_LOOKUP_COLUMN_OPTION = 'Refresh lookup columns';

export interface TableOpOptions {
  forRowRemoval?: boolean;
  forColumnEdit?: boolean;
  ignoreLockedDimensions?: boolean;
  /**
   * Default false. When true, recreates Single Input columns during the operation by unmerging and merging
   * around the TableOp. This should be true for most operations that deal with changing rows.
   */
  remakeSingleInputs?: boolean;
}

export type SingleDocTableOpFunction = (
  originalTableMap: FullTableMap,
  computedTables: OutputTableMap,
  targetTableName: string
) => SingleDocTableOpOutput;

export type TableOpFunction = (
  originalTable: Table,
  documentName: string | null,
  evaluatedTable: EvaluatedTable,
  tableMap: FullTableMap
) => {
  table: Table;
  tokenUpdaters: Array<TokenUpdater>;
  box: MultiSelectionBox | null;
  dim: TableDimension | null;
  tableOp?: TableOpDiffMap;
};

export interface TableOperation {
  index: number;
  op: TableOpFunction;
  options?: TableOpOptions;
}

export enum TableIndicatorStatus {
  ERROR = 'ERROR',
  IMMUTABLE = 'IMMUTABLE',
  NO_STATUS = 'NO_STATUS',
  OUT_OF_DATE = 'OUT_OF_DATE',
  ROWS_ARCHIVED = 'ROWS_ARCHIVED',
  TABLE_OR_SCHEMA_ARCHIVED = 'TABLE_OR_SCHEMA_ARCHIVED',
  UNSUBMITTED_CHANGES = 'UNSUBMITTED_CHANGES',
  HAS_BYPASSABLE_ERRORS = 'HAS_BYPASSABLE_ERRORS',
  SUBMITTED_BYPASSING_VALIDATION = 'SUBMITTED_BYPASSING_VALIDATION',
}

// The max length for a warehouse name is 58 chars, but user created columns do not sync to the warehouse
// 70 is an arbitrary number based on the longest names in use before the limit was enforced.
export const USER_CREATED_COLUMN_NAME_MAX_LENGTH = 70;

// Structured Table Columns that are created by the user after table insertion
// These columns are able to be deleted and renamed at any time
export const USER_CREATED_COLUMN_TYPES = [TableColumnType.CUSTOM, TableColumnType.DEDICATED_LOOKUP];

export enum FocusedTableViewMode {
  FULLSCREEN = 'FULLSCREEN',
  HORIZONTAL_SPLIT = 'HORIZONTAL_SPLIT',
}

// A partial DOMRect used to position the table cell editor.
export interface TableCellEditorPosition {
  top: number;
  left: number;
  minHeight: number;
  maxHeight: number;
  width: number;
}

/**
 * Metadata about a cell that was affected by a mutation.
 * Used for analytics tracking after the mutation is applied.
 */
export interface AffectedCell {
  row: number;
  column: number;
  oldCell: Cell | undefined;
  newCell: Cell;
}

/**
 * Result of a pure table mutation.
 */
export interface TableMutationResult {
  /** The new table after the mutation */
  table: Table;
  /** Operation descriptor for undo/redo tracking. null if no evaluation is needed. */
  tableOp: TableOpDiff | null;
  /** Cells that were affected, with before/after values for analytics */
  affectedCells: Array<AffectedCell>;
}
